import { z } from 'zod'
import { lenientNullable, timestampToDateNullable } from '@upadelmatch/core'

// `abuse_reports/{id}` — a player says another player did something wrong.
//
// WHY THIS EXISTS AT ALL. The /admin Moderación surface was designed and built
// against a mock store, and shipped that way: it rendered fabricated players
// and fabricated reports in PRODUCTION. That is the most dangerous kind of stub,
// because moderation is where somebody's public reputation gets changed — an
// admin acting confidently on an invented dossier writes a real consequence.
// There was also no way to FILE a report, so the queue could only ever be
// fiction.
//
// TWO TYPES, ONE QUEUE, because the operator's job is the same in both cases:
// look at a person, decide, record why.
//
//  · `no_show_disputed` — "I was marked absent and I was there." This one has a
//    SIDE EFFECT: upholding it decrements `players_public.noShows`, which is a
//    number strangers read when deciding whether to play with somebody. It is
//    the whole reason a dispute path has to exist; a wrong no-show is otherwise
//    permanent and unappealable.
//  · `behaviour` — conduct. No counter to correct; the outcome is a note, or
//    disabling the account.
//
// WHAT A REPORTER MAY NOT DO. Set the outcome. `status`, `resolution`,
// `resolvedAt` and `resolvedByUid` are operator territory, enforced in rules —
// otherwise filing a report and resolving it in your own favour is one write.
export const ABUSE_REPORT_TYPES = ['no_show_disputed', 'behaviour'] as const
export type AbuseReportType = (typeof ABUSE_REPORT_TYPES)[number]

/**
 * `open` → `upheld` | `dismissed`. Terminal states KEEP the document: it is the
 * record of a decision, and the same pair of players turning up again is
 * exactly the context an operator needs next time. Deleting a resolved report
 * would throw away the only history moderation has.
 */
export const ABUSE_REPORT_STATUSES = ['open', 'upheld', 'dismissed'] as const
export type AbuseReportStatus = (typeof ABUSE_REPORT_STATUSES)[number]

export const AbuseReportSchema = z.object({
  id: z.string(),
  reporterUid: z.string(),
  reportedUid: z.string(),
  type: lenientNullable(z.enum(ABUSE_REPORT_TYPES), 'abuseReport.type'),
  /** The match it happened in. Required for a disputed no-show — there is
   *  nothing to correct without one — and optional for behaviour. */
  matchId: z.string().nullable().default(null),
  /** The reporter's own words. Capped in rules, not just here: a schema runs
   *  on the client and can be skipped by anything writing directly. */
  detail: z.string().default(''),
  status: lenientNullable(z.enum(ABUSE_REPORT_STATUSES), 'abuseReport.status'),
  /** Why the operator decided what they decided. Written with the resolution,
   *  never before — an empty resolution on a resolved report is a decision
   *  nobody can review. */
  resolution: z.string().nullable().default(null),
  resolvedByUid: z.string().nullable().default(null),
  resolvedAt: timestampToDateNullable,
  /** Denormalised for the queue so the operator list needs one read, not N+1.
   *  Display-only — never trusted for identity, which is always the uid. */
  reporterName: z.string().nullable().default(null),
  reportedName: z.string().nullable().default(null),
  createdAt: timestampToDateNullable,
})

export type AbuseReport = z.infer<typeof AbuseReportSchema>

/** Rules enforce the same ceiling; keep the two in step. */
export const ABUSE_DETAIL_MAX = 600

export const ABUSE_TYPE_LABELS: Record<AbuseReportType, string> = {
  no_show_disputed: 'Ausencia disputada',
  behaviour: 'Comportamiento',
}

export const ABUSE_STATUS_LABELS: Record<AbuseReportStatus, string> = {
  open: 'Abierta',
  upheld: 'Resuelta a favor',
  dismissed: 'Descartada',
}
